import React from 'react';
import { Column, Tab, Level } from 'rbx';
import { useStoreState } from 'easy-peasy';
import styled from 'styled-components';
import { format, parse } from 'date-fns';

import Layout from '../../components/global/Layout';
import { Tabs, Heading, CheckBox } from '../../components/elements';
import {
  fetchAggregateMenuOrderDrilldown,
  fetchAggregateMenuOrderRollup,
  transformDrilldownData,
  transformRollupData,
} from './helpers';
import RollupTable from './RollupTable';
import DrilldownTable from './DrilldownTable';

const DATE_FORMAT = 'EEE, d MMM yyyy';

const getDateRange = (rangeType, drilldownDataTimeFrame, loading) => {
  if (loading) {
    return null;
  }

  if (Object.values(drilldownDataTimeFrame).every(v => !v)) {
    return null;
  }

  const fmt = d => format(parse(d, 'dd/MM/yyyy', new Date()), DATE_FORMAT);

  if (rangeType === 'TODAY') {
    const label = fmt(drilldownDataTimeFrame.from_date);
    return { from: label, to: label };
  }

  if (rangeType === 'YESTERDAY') {
    const label = fmt(drilldownDataTimeFrame.from_date);
    return { from: label, to: label };
  }

  if (
    rangeType === 'WEEK' ||
    rangeType === 'MONTH' ||
    rangeType === 'PREVIOUS_MONTH' ||
    rangeType === 'YEAR' ||
    rangeType === 'PREVIOUS_YEAR'
  ) {
    return {
      from: fmt(drilldownDataTimeFrame.from_date),
      to: fmt(drilldownDataTimeFrame.to_date),
    };
  }

  return null;
};

const DateRangeLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: #f0f7ff;
  border-left: 3px solid #00aeef;
  font-size: 0.85rem;
  color: #4a4a4a;
`;

const DateRangeLabel = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const Wrapper = styled.div`
  padding: 1rem 1rem;
  border-bottom: 1px solid #f6f6f6;
`;

const Header = () => (
  <Wrapper>
    <Level>
      <Level.Item align="left">
        <Heading>Dashboard</Heading>
      </Level.Item>
    </Level>
  </Wrapper>
);

const Home = () => {
  const { userId } = useStoreState(state => state.auth);
  const [loading, setLoading] = React.useState(true);
  const [rollupTableData, setRollupTableData] = React.useState(null);
  const [drilldownTableData, setDrilldownTableData] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('TODAY');
  const [excludeTestPlace, setExcludeTestPlace] = React.useState(false);
  const [isCashExclude, setIsCashExclude] = React.useState(false);

  const drilldownDataTimeFrame = {
    from_date: drilldownTableData?.[0]?.from_date,
    to_date: drilldownTableData?.[0]?.to_date,
  };

  React.useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      const [
        aggregateMenuOrderRollupData,
        aggregateMenuOrderDrilldownData,
      ] = await Promise.all([
        fetchAggregateMenuOrderRollup({
          userId,
          rangeType: activeTab,
          isTestPlace: !excludeTestPlace,
          isCashExclude,
        }),
        fetchAggregateMenuOrderDrilldown({
          userId,
          rangeType: activeTab,
          isTestPlace: !excludeTestPlace,
          isCashExclude,
        }),
      ]);
      console.log('aggregate data...');
      console.log('aggregateMenuOrderRollupData', aggregateMenuOrderRollupData);
      console.log(
        'aggregateMenuOrderDrilldownData',
        aggregateMenuOrderDrilldownData,
      );

      if (aggregateMenuOrderRollupData) {
        try {
          const transformData1 = transformRollupData(
            aggregateMenuOrderRollupData,
          );
          setRollupTableData(transformData1);
        } catch {
          setLoading(false);
        }
      } else {
        setRollupTableData(null);
      }

      if (aggregateMenuOrderDrilldownData) {
        try {
          const transformData2 = transformDrilldownData(
            aggregateMenuOrderDrilldownData,
          );
          setDrilldownTableData(transformData2);
        } catch {
          setLoading(false);
        }
      } else {
        setDrilldownTableData(null);
      }

      setLoading(false);
    };
    fetchPageData();
  }, [activeTab, excludeTestPlace, isCashExclude]);

  return (
    <Layout>
      <Header />
      <Column.Group
        style={{ padding: '1rem 1rem 0 1rem' }}
        vcentered
        multiline
        gapSize={8}
      >
        <Column size={3}>
          <CheckBox
            label="Exclude Test Restaurants"
            value={excludeTestPlace}
            onChange={value => {
              setExcludeTestPlace(value);
            }}
          />
        </Column>
        <Column size={3}>
          <CheckBox
            label="Exclude Cash Orders"
            name="event_business"
            value={isCashExclude}
            onChange={value => {
              setIsCashExclude(value);
            }}
          />
        </Column>
      </Column.Group>
      <RollupTable loading={loading} rollupTableData={rollupTableData} />
      <br />
      <Tabs className="tabs is-medium is-fullwidth">
        <Tab
          active={activeTab === 'TODAY'}
          onClick={() => setActiveTab('TODAY')}
        >
          TODAY
        </Tab>
        <Tab
          active={activeTab === 'YESTERDAY'}
          onClick={() => setActiveTab('YESTERDAY')}
        >
          YESTERDAY
        </Tab>
        <Tab active={activeTab === 'WEEK'} onClick={() => setActiveTab('WEEK')}>
          THIS WEEK
        </Tab>
        <Tab
          active={activeTab === 'MONTH'}
          onClick={() => setActiveTab('MONTH')}
        >
          THIS MONTH
        </Tab>
        <Tab
          active={activeTab === 'PREVIOUS_MONTH'}
          onClick={() => setActiveTab('PREVIOUS_MONTH')}
        >
          PREVIOUS MONTH
        </Tab>
        <Tab active={activeTab === 'YEAR'} onClick={() => setActiveTab('YEAR')}>
          THIS YEAR
        </Tab>
        <Tab
          active={activeTab === 'PREVIOUS_YEAR'}
          onClick={() => setActiveTab('PREVIOUS_YEAR')}
        >
          PREVIOUS YEAR
        </Tab>
      </Tabs>
      {(() => {
        const range = getDateRange(activeTab, drilldownDataTimeFrame, loading);
        if (!range) return null;
        const isSingleDay = range.from === range.to;
        return (
          <DateRangeLegend>
            <span role="img" aria-label="calendar">
              📅
            </span>
            {isSingleDay ? (
              <>
                Showing data for&nbsp;
                <DateRangeLabel>{range.from}</DateRangeLabel>
              </>
            ) : (
              <>
                Showing data from&nbsp;
                <DateRangeLabel>{range.from}</DateRangeLabel>
                &nbsp;to&nbsp;
                <DateRangeLabel>{range.to}</DateRangeLabel>
              </>
            )}
          </DateRangeLegend>
        );
      })()}
      <DrilldownTable
        loading={loading}
        drilldownTableData={drilldownTableData}
      />
    </Layout>
  );
};

export default Home;
